'use client'

import { useMemo, useState } from 'react'
import useSWR from 'swr'

export type SavingsTimeScale = 'End of 2024' | '+1Y' | '+5Y'

const INITIAL_AMOUNT = 100

interface WeeklyYieldResponse {
  weeklyYields: {
    timestamp: string
    value: number
    treasury_value: number
    yearly_avg_apy: number
    treasury_avg_apy: number
  }[]
}

function calculateValue(currentValue: number, weeklyAPY: number): number {
  const weeklyRate = weeklyAPY / 100 / 52
  return currentValue * (1 + weeklyRate)
}

// Generate estimated points for future dates
function generateEstimatedPoints(
  startDate: Date,
  startValue: number,
  startTreasuryValue: number,
  yearlyAvgApy: number,
  treasuryAvgApy: number,
  weeksToProject: number,
) {
  const points = []
  let currentDate = new Date(startDate)
  let currentValue = startValue
  let currentTreasuryValue = startTreasuryValue

  const weeklySusdeRate = yearlyAvgApy / 100 / 52
  const weeklyTreasuryRate = treasuryAvgApy / 100 / 52

  for (let i = 0; i < weeksToProject; i++) {
    currentDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000)
    currentValue *= 1 + weeklySusdeRate
    currentTreasuryValue *= 1 + weeklyTreasuryRate

    points.push({
      date: currentDate.getTime(),
      'Estimated sUSDe': currentValue,
      'Estimated Treasuries': currentTreasuryValue,
      'Estimated USDT/USDC': INITIAL_AMOUNT,
      sUSDe: null,
      Treasuries: null,
      'USDT/USDC': null,
    })
  }

  return points
}

export const useSavingsData = () => {
  const [timeScale, setTimeScale] = useState<SavingsTimeScale>('End of 2024')

  const { data: historicalData } = useSWR<WeeklyYieldResponse>(
    '/api/yields/historical/historical-susde-and-competitor-yields',
  )

  const { data, headlineSavingValue } = useMemo(() => {
    if (!historicalData?.weeklyYields?.length) return { data: [], headlineSavingValue: 0 }

    let susdeValue = INITIAL_AMOUNT
    let treasuryValue = INITIAL_AMOUNT

    const historicalPoints = historicalData.weeklyYields.map(weekData => {
      const date = new Date(weekData.timestamp)

      susdeValue = calculateValue(susdeValue, weekData.value)
      treasuryValue = calculateValue(treasuryValue, weekData.treasury_value)

      return {
        date: date.getTime(),
        'Estimated sUSDe': null,
        'Estimated Treasuries': null,
        'Estimated USDT/USDC': null,
        sUSDe: susdeValue,
        Treasuries: treasuryValue,
        'USDT/USDC': INITIAL_AMOUNT,
      }
    })

    const lastPoint = historicalPoints[historicalPoints.length - 1]

    // For 'End of 2024' view, return only historical data
    if (timeScale === 'End of 2024') {
      return {
        data: historicalPoints,
        headlineSavingValue: lastPoint.sUSDe,
      }
    }

    // Get last historical point for estimates
    const weeksToProject = timeScale === '+1Y' ? 52 : 260 // 1 year or 5 years

    // Generate estimated points
    const estimatedPoints = generateEstimatedPoints(
      new Date(lastPoint.date),
      lastPoint.sUSDe,
      lastPoint.Treasuries,
      historicalData.weeklyYields[0].yearly_avg_apy,
      historicalData.weeklyYields[0].treasury_avg_apy,
      weeksToProject,
    )

    // For the transition point, show both actual and estimated
    const transitionPoint = {
      ...lastPoint,
      'Estimated sUSDe': lastPoint.sUSDe,
      'Estimated Treasuries': lastPoint.Treasuries,
      'Estimated USDT/USDC': INITIAL_AMOUNT,
    }

    // Replace last historical point with transition point and add estimated points
    return {
      data: [...historicalPoints.slice(0, -1), transitionPoint, ...estimatedPoints],
      headlineSavingValue: lastPoint.sUSDe,
    }
  }, [historicalData, timeScale])

  return { chartData: data?.length ? data : undefined, headlineSavingValue, setTimeScale, timeScale }
}
